import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import { DomainContext } from 'common/layouts/LayoutContext';
import Languages from 'common/utils/language-utils/index';
import { useLanguageSetting } from 'common/hooks/useLanguageSetting';
import { Bar } from 'common/layouts/AuthLayout/Bar';
import NavItem from 'common/layouts/NavItem';
import { SettingsNavData } from './constants/SettingsNavData';
import { useSidebar } from 'common/contexts/SidebarContext';
import SidebarToggle from 'common/components/SidebarToggle';
import { filterSettingsNavData } from 'common/layouts/AuthLayout/utils/NavUtils';
import { useSelector } from 'react-redux';
import {
  isCurrentUserRegularUser,
  isCurrentUserStaffOrSuperAdmin,
  isCurrentUserSuperUser,
} from 'common/selectors/auth';
import useFeatures from 'common/hooks/useFeatures';

const SettingsSidebar = () => {
  const location = useLocation();
  const Domain = useContext(DomainContext);
  const language_hook = new Languages(useLanguageSetting());
  const { isOpen } = useSidebar();

  const isStaff = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isSuperuser = useSelector(isCurrentUserSuperUser);
  const isRegularUser = useSelector(isCurrentUserRegularUser);

  const { features } = useFeatures();

  const navData = useMemo(
    () => filterSettingsNavData(SettingsNavData, isSuperuser, isStaff, isRegularUser, features),
    [isSuperuser, isStaff, features]
  );

  return (
    <SidebarContainer isOpen={isOpen}>
      <Bar isOpen={isOpen}>
        <LogoContainer isOpen={isOpen}>
          <Logo data-testid="logo" src={Domain.image} />
          <LogoText isOpen={isOpen}>nuLogic</LogoText>
        </LogoContainer>
        <NavScrollArea>
          {navData.map((nav, i) => (
            <NavItem
              key={i}
              navItem={nav.sidebarItem}
              isDisabled={nav.isDisabled}
              selected={location.pathname}
              isFrench={() => language_hook.is_french()}
              data-testid={`settings-nav-item-${nav.sidebarItem.id}`}
              isOpen={isOpen}
            />
          ))}
        </NavScrollArea>
        <SidebarToggle />
      </Bar>
    </SidebarContainer>
  );
};

const SidebarContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 1000;
  transition: width 0.3s ease-in-out;
  width: ${props => (props.isOpen ? '250px' : '50px')};
`;

const LogoContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  padding: 5px 0;
  margin-bottom: 20px;
  transition: all 0.3s ease-in-out;
  justify-content: ${props => (props.isOpen ? 'flex-start' : 'center')};
  padding-left: ${props => (props.isOpen ? '18px' : '0')};
  width: 100%;
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const LogoText = styled.div<{ isOpen: boolean }>`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #1e2172;
  margin-left: 10px;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  white-space: nowrap;
  display: ${props => (props.isOpen ? 'block' : 'none')};
`;

const NavScrollArea = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
`;

export default SettingsSidebar;
